/// Sextant-altitude correction chain — converts the raw reading off
/// the sextant (Hs) into the observed altitude (Ho) the navigator
/// actually plots a sight from.
///
/// The canonical order (and the one we teach):
///
///   Hs                        — sextant altitude
///   −  index error            — fixed instrument error
///   = Hi                      — instrument-corrected altitude
///   −  dip                    — for height of eye above the sea
///   = Ha                      — apparent altitude
///   −  refraction             — bend of light by the atmosphere
///   ±  semi-diameter          — LL: + ;  UL: −  (Sun, Moon)
///   +  parallax               — body's distance vs Earth's radius
///   = Ho                      — observed altitude
///
/// Phase 1 covers the Sun (lower- and upper-limb). Moon / planets /
/// stars follow in Phase 3.

import { DEG, normalize360 } from "./time";
import { sunSemiDiameterArcmin } from "./sun";
import { moonPosition } from "./moon";
import type { Planet } from "./planets";

export type SunLimb = "LL" | "UL";

export type Body =
  /// The Sun. `limb` tells the chain whether SD is added (lower-limb,
  /// the bright lower edge brought down to the horizon) or subtracted
  /// (upper-limb, used when the bottom of the disc is washed out by
  /// dust or haze).
  | { kind: "sun"; limb: SunLimb }
  /// The Moon. Same limb conventions as the Sun. The Moon's parallax
  /// is big (~57′ vs 0.14′ for the Sun) and its SD swings with
  /// distance, so a date is strongly recommended.
  | { kind: "moon"; limb: SunLimb }
  /// A navigational planet (Venus / Mars / Jupiter / Saturn). Point
  /// source — no SD, and parallax is sub-arc-minute even at closest
  /// approach, so the chain stops after refraction.
  | { kind: "planet"; planet: Planet }
  /// A navigational star. Point source like a planet — chain stops
  /// after refraction.
  | { kind: "star" };

export interface SextantInput {
  body: Body;
  /// Sextant reading in decimal degrees.
  hsDeg: number;
  /// Signed index error in arc-minutes — positive = "on the arc"
  /// (subtract from Hs); negative = "off the arc" (add to Hs).
  indexErrorMin: number;
  /// Height of eye above the sea, metres. 0 collapses dip to 0.
  heightOfEyeM: number;
  /// Optional non-standard atmosphere — standard is 1010 hPa, 10°C.
  pressureHPa?: number;
  temperatureC?: number;
  /// Used for date-aware semi-diameter (the Sun's SD varies ±0.3'
  /// through the year). Omit and the average SD of 16.0' is used.
  date?: Date;
}

export interface CorrectionStep {
  /// Plain-English label for the calculator's working display.
  label: string;
  /// Arc-value entering this step, in decimal degrees.
  fromDeg: number;
  /// Signed delta applied this step, in arc-minutes. Positive adds.
  deltaMin: number;
  /// Arc-value leaving this step, in decimal degrees.
  toDeg: number;
}

export interface SextantResult {
  /// Observed altitude (Ho) — the value you plot a position line from.
  hoDeg: number;
  /// The step-by-step working, in the order the navigator applies them.
  steps: readonly CorrectionStep[];
  /// Individual correction magnitudes (arc-minutes), broken out so
  /// the UI can label them next to the working.
  indexErrorMin: number;
  dipMin: number;
  refractionMin: number;
  semiDiameterMin: number;
  parallaxMin: number;
}

const STANDARD_PRESSURE_HPA = 1010;
const STANDARD_TEMPERATURE_C = 10;

/// Dip of the horizon for a height of eye above the sea, in arc-min.
/// Bowditch's formula: dip(') ≈ 1.76·√h(m). Negative or zero heights
/// collapse to 0 (defensive — the calculator's input is clamped to
/// non-negative anyway).
export function dipArcmin(heightOfEyeM: number): number {
  if (heightOfEyeM <= 0) return 0;
  return 1.76 * Math.sqrt(heightOfEyeM);
}

/// Atmospheric refraction at the given apparent altitude, in arc-min.
/// Bennett's 1982 formula — standard at 10°C / 1010 hPa, with a
/// pressure/temperature factor for non-standard conditions.
///
/// Above the horizon only — for Ha < 0 we return 0 (the formula is
/// only defined for visible objects).
export function refractionArcmin(
  haDeg: number,
  pressureHPa = STANDARD_PRESSURE_HPA,
  temperatureC = STANDARD_TEMPERATURE_C,
): number {
  if (haDeg < 0) return 0;
  const r = 1 / Math.tan(DEG * (haDeg + 7.31 / (haDeg + 4.4)));
  // PT factor: thinner air bends light less; warmer air likewise.
  const pt = (pressureHPa / 1010) * (283 / (273 + temperatureC));
  return r * pt;
}

/// Sun's semi-diameter for a given date (date-aware) or the yearly
/// average. Arc-minutes.
export function sunSemiDiameter(date?: Date): number {
  return date ? sunSemiDiameterArcmin(date) : 15.994;
}

/// Sun's parallax in altitude — the small correction for the
/// observer's offset from Earth's centre. Always additive.
/// HP_sun ≈ 0.144'; PA = HP·cos(Ha).
function sunParallaxArcmin(haDeg: number): number {
  return 0.144 * Math.cos(DEG * haDeg);
}

/// Run the full Hs → Ho chain. Returns Ho plus a `steps[]` trail the
/// calculator UI can render as the working line-by-line.
export function correctSextant(input: SextantInput): SextantResult {
  const { hsDeg, indexErrorMin, heightOfEyeM, body } = input;
  const steps: CorrectionStep[] = [];

  // Step 1: index error.
  // Positive IE = "on the arc" = subtract from Hs.
  const hiDeg = hsDeg - indexErrorMin / 60;
  steps.push({
    label: "Index error",
    fromDeg: hsDeg,
    deltaMin: -indexErrorMin,
    toDeg: hiDeg,
  });

  // Step 2: dip — always subtractive.
  const dip = dipArcmin(heightOfEyeM);
  const haDeg = hiDeg - dip / 60;
  steps.push({
    label: "Dip",
    fromDeg: hiDeg,
    deltaMin: -dip,
    toDeg: haDeg,
  });

  // Step 3: refraction — subtractive, depends on apparent altitude.
  const refraction = refractionArcmin(
    haDeg,
    input.pressureHPa ?? STANDARD_PRESSURE_HPA,
    input.temperatureC ?? STANDARD_TEMPERATURE_C,
  );
  const afterRefDeg = haDeg - refraction / 60;
  steps.push({
    label: "Refraction",
    fromDeg: haDeg,
    deltaMin: -refraction,
    toDeg: afterRefDeg,
  });

  // Steps 4 + 5: semi-diameter and parallax (body-specific).
  let semiDiameter = 0;
  let parallax = 0;
  let hoDeg = afterRefDeg;

  if (body.kind === "sun") {
    semiDiameter = sunSemiDiameter(input.date);
    const sdSigned = body.limb === "LL" ? +semiDiameter : -semiDiameter;
    const afterSdDeg = afterRefDeg + sdSigned / 60;
    steps.push({
      label: body.limb === "LL" ? "Semi-diameter (LL +)" : "Semi-diameter (UL −)",
      fromDeg: afterRefDeg,
      deltaMin: sdSigned,
      toDeg: afterSdDeg,
    });

    parallax = sunParallaxArcmin(haDeg);
    hoDeg = afterSdDeg + parallax / 60;
    steps.push({
      label: "Parallax",
      fromDeg: afterSdDeg,
      deltaMin: +parallax,
      toDeg: hoDeg,
    });
  } else if (body.kind === "moon") {
    // Moon SD and HP both come from the Moon's distance, so a date is
    // strongly recommended. Without one, fall back to average values.
    const moon = input.date ? moonPosition(input.date) : null;
    semiDiameter = moon ? moon.sdArcmin : 15.7;
    const hp = moon ? moon.hpArcmin : 57.0;

    const sdSigned = body.limb === "LL" ? +semiDiameter : -semiDiameter;
    const afterSdDeg = afterRefDeg + sdSigned / 60;
    steps.push({
      label: body.limb === "LL" ? "Semi-diameter (LL +)" : "Semi-diameter (UL −)",
      fromDeg: afterRefDeg,
      deltaMin: sdSigned,
      toDeg: afterSdDeg,
    });

    // PA ≈ HP·cos(Ha). The Moon's parallax dominates every other
    // correction on a Moon sight.
    parallax = hp * Math.cos(DEG * haDeg);
    hoDeg = afterSdDeg + parallax / 60;
    steps.push({
      label: "Parallax (HP·cos Ha)",
      fromDeg: afterSdDeg,
      deltaMin: +parallax,
      toDeg: hoDeg,
    });
  }

  return {
    hoDeg: normalize360(hoDeg),
    steps,
    indexErrorMin,
    dipMin: dip,
    refractionMin: refraction,
    semiDiameterMin: semiDiameter,
    parallaxMin: parallax,
  };
}
